import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { startAutoSync, pullSnapshot } from "@/offline/sync";
import { cacheOfflineBundle, syncOutbox, kickSyncScheduler, stopSyncScheduler } from "@/offline/outbox";

const AuthCtx = createContext({
  user: null,
  workspace: null,
  workspaces: [],
  entitlements: null,
  loading: true,
  loginEmail: async () => {},
  signup: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [role, setRole] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  const [landingPath, setLandingPath] = useState("/app");
  const [isStaffPin, setIsStaffPin] = useState(false);
  const [tourSeen, setTourSeen] = useState({ v1: {} });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspace(data.workspace);
      setWorkspaces(data.workspaces || []);
      setRole(data.role || null);
      setEntitlements(data.entitlements || null);
      setEffectivePermissions(data.effective_permissions || []);
      setLandingPath(data.landing_path || "/app");
      setIsStaffPin(!!data.is_staff_pin);
      setTourSeen(data.tour_seen || { v1: {} });
      if (typeof window !== "undefined") {
        window.__bizone_entitlements = data.entitlements || null;
        window.__bizone_effective_perms = data.effective_permissions || [];
      }
      if (data.workspace?.workspace_id) {
        localStorage.setItem("bos_workspace_id", data.workspace.workspace_id);
      }
      return data;
    } catch {
      setUser(null);
      setWorkspace(null);
      setWorkspaces([]);
      setRole(null);
      setEntitlements(null);
      setEffectivePermissions([]);
      setLandingPath("/app");
      setIsStaffPin(false);
      setTourSeen({ v1: {} });
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip /me if returning from OAuth (AuthCallback will handle it)
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  // Bootstrap offline sync once a workspace is known
  useEffect(() => {
    if (!workspace?.workspace_id) return;
    startAutoSync(workspace.workspace_id);
    // First-time snapshot pull (fire-and-forget; safe to run on every login)
    pullSnapshot(workspace.workspace_id).catch(() => {});
    // Phase 5: fetch & cache the offline bundle (products, PIN verifiers,
    // inventory states, entitlement) scoped to this workspace + device.
    const deviceId = (typeof localStorage !== "undefined") && localStorage.getItem("bos_pos_device_id");
    if (deviceId) {
      api.get(`/offline/bundle?device_id=${encodeURIComponent(deviceId)}`)
        .then(({ data }) => cacheOfflineBundle(data))
        .catch(() => {});
    }
    // Attempt to flush any pending outbox events now that we're authenticated.
    syncOutbox(workspace.workspace_id).catch(() => {});
    // Phase 5 — adaptive sync scheduler: only runs while pending events exist.
    kickSyncScheduler(workspace.workspace_id);
    return () => stopSyncScheduler();
  }, [workspace?.workspace_id]);

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("bos_access_token", data.access_token);
    if (data.workspace?.workspace_id) {
      localStorage.setItem("bos_workspace_id", data.workspace.workspace_id);
    }
    setUser(data.user);
    setWorkspace(data.workspace);
    await refresh();
    return data;
  };

  const signup = async ({ email, password, name, workspace_name }) => {
    const { data } = await api.post("/auth/signup", { email, password, name, workspace_name });
    localStorage.setItem("bos_access_token", data.access_token);
    if (data.workspace?.workspace_id) {
      localStorage.setItem("bos_workspace_id", data.workspace.workspace_id);
    }
    setUser(data.user);
    setWorkspace(data.workspace);
    await refresh();
    return data;
  };

  // V4-C — Mobile + 6-digit PIN signup/login for new Filipino SMB owners.
  const ownerSignup = async ({ business_name, mobile, pin, confirm_pin }) => {
    const { data } = await api.post("/auth/owner/signup", { business_name, mobile, pin, confirm_pin });
    localStorage.setItem("bos_access_token", data.access_token);
    if (data.workspace?.workspace_id) {
      localStorage.setItem("bos_workspace_id", data.workspace.workspace_id);
    }
    setUser(data.user);
    setWorkspace(data.workspace);
    await refresh();
    return data;
  };

  const ownerLogin = async ({ mobile, pin }) => {
    const { data } = await api.post("/auth/owner/login", { mobile, pin });
    localStorage.setItem("bos_access_token", data.access_token);
    if (data.workspace?.workspace_id) {
      localStorage.setItem("bos_workspace_id", data.workspace.workspace_id);
    }
    setUser(data.user);
    setWorkspace(data.workspace);
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("bos_access_token");
    localStorage.removeItem("bos_workspace_id");
    setUser(null);
    setWorkspace(null);
    setWorkspaces([]);
    setTourSeen({ v1: {} });
  };

  return (
    <AuthCtx.Provider value={{ user, workspace, workspaces, role, entitlements, effectivePermissions, landingPath, isStaffPin, tourSeen, loading, loginEmail, signup, ownerSignup, ownerLogin, logout, refresh, setWorkspace, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
